import React, { useEffect, useState } from "react";
import NextError from "next/error";

import type { GetServerSideProps } from "next";
import nr from "newrelic";
import type { Topic, TopicFacade } from "@foundation/artemis-client";
import { parseGeoInfo } from "@foundation/sparkle/lib/adDials/parseGeoInfo";

import { QuickAddProvider } from "@foundation/quickadd-provider";
import setTickerCookie from "~utils/setTickerCookie";
import { QuoteContext, type QuoteContextType } from "~context/QuoteContext";
import { QuoteCompareProvider } from "~context/QuoteCompareContext";
import { Layout } from "~components/Layout";
import { RightRail } from "~components/RightRail";
import { LeftRail } from "~components/LeftRail";
import QuotePageLayout from "~components/QuotePageLayout";
import type {
  BoardMembersAndExecutivesData,
  FormattedBarChartSecurity,
  PaywallType,
  QuoteData,
} from "~types/types";
import { PersonalizedRecircBoxSmallList } from "~components/PersonalizedModule/PersonalizedModule";
import { loadPrerequisiteData, createQuoteServerSideProps } from "~data/quote";
import {
  ALLOWED_QUOTE_PAGE_TABS,
  DEFAULT_QUOTE_PAGE_TAB,
  SECURITY_TYPES_TO_NAMES,
} from "~constants";
import formatRealTimeData from "~utils/client/formatRealTimeData";
import { PageWrapper, PageWrapperProps } from "src/~components/PageWrapper";
import { getQuoteStructuredData } from "~utils/seo/structuredData";
import getTickerIdFromQuery from "~utils/getTickerIdFromQuery";
import { SnapshotsChartsProvider } from "~context/SnapshotChartsContext";
import { AbbaProviderWithUserInfo } from "~components/AbbaProviderWithUserInfo";
import { getAdTargeting } from "~utils/AdTargetingClient";
import { getCurrentUrl } from "~utils/nextContextUtil";
import { getAdConfig } from "~api/getAdConfig";
import { getAdCode } from "~utils/adUtils";
import { getValidLocale, getLanguageCodeLocale } from "~constants/i18n";

const QUOTE_UPDATE_INTERVAL = Number(
  process.env.NEXT_PUBLIC_QUOTE_UPDATE_INTERVAL ?? 0
);

const IS_ARTEMIS_DISABLED = "true" === process.env.NEXT_PUBLIC_DISABLE_ARTEMIS;
const ARTEMIS_POLL_INTERVAL = Number(
  process.env.NEXT_PUBLIC_ARTEMIS_POLL_INTERVAL ?? 5000
);

const payWallType: PaywallType = "additive";
const PAGE_TYPE = "quote";
const CONTENT_TYPE = "QUOTE";
const SEARCH_CLIENT_URL =
  process.env.NEXT_PUBLIC_SEARCH_CLIENT_URL || "https://search.bloomberg.com/";

const WATCHLIST_SERVICE_URL = `${
  process.env.NEXT_PUBLIC_REGISTRATION_URL || "https://login.bloomberg.com"
}/watchlist-service`;

export type QuoteProps = PageWrapperProps & {
  tickerId: string;
  quote?: QuoteData;
  boardMembersAndExecutives?: BoardMembersAndExecutivesData;
  barCharts?: FormattedBarChartSecurity;
  chartNews?: any;
  locale?: string;
  context?: string | string[];
  indexMembers?: unknown;
};

async function fetchQuoteData(
  tickerId: string,
  securityType: string,
  locale: string
) {
  const quoteApi = `/bazaar/api/quote/${securityType}/${tickerId}?locale=${locale}`;

  const response = await fetch(quoteApi, {
    credentials: "same-origin",
  });

  if (!response.ok) return null;

  return response.json();
}

export function normalizeQuotePageTab(rawTab?: string | string[] | null) {
  const tab = Array.isArray(rawTab) ? rawTab[0] : rawTab;

  if (typeof tab !== "string") return DEFAULT_QUOTE_PAGE_TAB;

  return ALLOWED_QUOTE_PAGE_TABS.includes(tab) ? tab : DEFAULT_QUOTE_PAGE_TAB;
}

export const getServerSideProps: GetServerSideProps<QuoteProps> = async (
  context
) => {
  const { req, res, locale, query } = context;

  const quotePageTab = normalizeQuotePageTab(query.activeTab);
  const tickerId = getTickerIdFromQuery(query.ticker);

  const languageCodeLocale = getLanguageCodeLocale(locale);
  const validLocale = getValidLocale(languageCodeLocale);

  if (!tickerId) {
    nr.addCustomAttributes({
      securityStatus: "Not Found",
      invalidTicker: true,
    });

    return {
      notFound: true,
    };
  }

  /**
   * This cookie is to be used for securing API endpoints
   */
  setTickerCookie(tickerId, req, res);

  nr.addCustomAttributes({
    ticker: tickerId,
    pageSubType: quotePageTab,
  });

  const prerequisiteData = await loadPrerequisiteData(tickerId);
  const securityType = prerequisiteData?.type || "";

  if (prerequisiteData?.companyIsPrivate) {
    return {
      redirect: {
        permanent: true,
        destination: `/profile/company/${tickerId}`,
      },
    };
  }

  const quoteServerSideProps = createQuoteServerSideProps(validLocale);
  const quotePageProps = await quoteServerSideProps.load<QuoteProps>({
    tickerId,
    securityType,
    pageType: "quote-bazaar",
    quotePageTab,
    context: ["bazaar", "media-ui-core", "media-ui-markets", "media-ui-nav"],
  });

  if (!quotePageProps.quote) {
    nr.addCustomAttributes({
      securityStatus: "Not Found",
    });

    return {
      notFound: true,
    };
  }

  if (quotePageProps.partial) {
    res.statusCode = 304;
    res.setHeader("Cache-Control", "no-cache");

    nr.addCustomAttributes({
      partialDataLoad: true,
    });

    return { props: { partial: true } as QuoteProps };
  }

  // show 404 when index members are not available for current ticker
  if (quotePageTab === "members" && !quotePageProps.indexMembers) {
    nr.addCustomAttributes({
      securityStatus: "Found",
      securityMembers: "Not Found",
    });

    return {
      notFound: true,
    };
  }

  const adCode = getAdCode("quote-bazaar", undefined, validLocale);
  const adTargeting = await getAdTargeting({
    tickerId,
    pageId: quotePageProps.quote.id,
    adPageType: "quote",
    url: getCurrentUrl(context),
    adCode,
  });
  const adConfig = getAdConfig({
    ...parseGeoInfo(context.req.cookies?.geo_info),
    adCode,
  });

  return {
    props: {
      ...quotePageProps,
      tickerId,
      pageType: "quote-bazaar",
      countryCode: context.req.cookies?.country_code || "US",
      adTargeting,
      adConfig,
      pageData: {
        quotePageTab,
      },
    },
  };
};

export default function Quote(props: QuoteProps) {
  const {
    pageType,
    quote,
    indexMembers,
    boardMembersAndExecutives,
    countryCode,
    locale,
  } = props;

  useEffect(() => {
    if (!quote) {
      return;
    }

    const { id, mediaSecurityType, mediaSecuritySubtype } = quote;

    if (!mediaSecurityType) {
      const { name, type, exchangeId, lastUpdate, isAfterHours, marketStatus } =
        quote;

      window?.newrelic?.noticeError(new Error("Missing mediaSecurityType!"), {
        countryCode,
        exchangeId,
        id,
        isAfterHours,
        lastUpdate,
        marketStatus,
        name,
        type,
      });
    }
    /*
     * IMPORTANT: DO NOT REMOVE.
     *
     * This triggers the "Personalization Tracking - Resource View"
     * GTM tag which drives Recently Viewed Securities.
     *
     * The triggered GTM tag will format a tracking call to the following:
     * https://tracking.bloomberg.com/psz/pixel.gif
     */
    window.dataLayer?.push({
      event: "Track Request - PV Quote",
      resourceType: mediaSecurityType,
      resourceSubType: mediaSecuritySubtype,
      resourceId: id,
      referrer: document?.referrer,
    });
  }, [countryCode, quote]);

  const [isRealTime, setRealTime] = useState(
    quote?.realTimeTickerDescription && !IS_ARTEMIS_DISABLED
  );
  const [quoteContextValue, setQuoteContextValue] = useState<QuoteContextType>({
    ...quote,
    ...boardMembersAndExecutives,
    searchClientURL: SEARCH_CLIENT_URL,
    pageType,
    indexMembers,
    ...{ isRealTime, countryCode },
  } as QuoteContextType);
  const [quoteData, setQuoteData] = useState(quote);
  const [artemisClient, setArtemisClient] = useState<TopicFacade>();

  const startPeriodicUpdate = () => {
    if (isRealTime) return undefined;
    const localeString = locale || "en";

    if (!quoteData?.id) return undefined;
    if (!QUOTE_UPDATE_INTERVAL) return undefined;

    return setInterval(() => {
      fetchQuoteData(quoteData?.id, quoteData?.type, localeString).then(
        (updatedData) => {
          if (!updatedData) return;
          setQuoteData(updatedData);
        }
      );
    }, QUOTE_UPDATE_INTERVAL);
  };

  let updateTimer: string | number | NodeJS.Timeout | undefined = undefined;

  const tabVisibilityHandler = () => {
    if (document.hidden) {
      clearInterval(updateTimer);
      updateTimer = undefined;
    } else if (updateTimer === undefined) {
      updateTimer = startPeriodicUpdate();
    }
  };

  const formatAndMergeRealTimeData = (topic: Topic) => {
    const formattedRTData = formatRealTimeData(topic, quote);

    const combinedData = { ...quote, ...formattedRTData };

    setQuoteData(combinedData as QuoteData);
    setQuoteContextValue({ ...quoteContextValue, ...combinedData });
  };

  useEffect(() => {
    document.addEventListener("visibilitychange", tabVisibilityHandler);

    if (!navigator?.cookieEnabled) {
      return undefined;
    }

    if (updateTimer === undefined) {
      updateTimer = startPeriodicUpdate();
    }

    if (!IS_ARTEMIS_DISABLED) {
      try {
        window.artemis.cmd?.push(() => {
          setArtemisClient(
            window.getArtemisClient({
              pollingInterval: ARTEMIS_POLL_INTERVAL,
            })
          );
        });
      } catch (e) {
        console.log("failed to initialize artemis");

        return undefined;
      }
    }

    return () => {
      clearInterval(updateTimer);
      updateTimer = undefined;
      document.removeEventListener("visibilitychange", tabVisibilityHandler);
    };
  }, []);

  useEffect(() => {
    if (artemisClient && quote?.realTimeTickerDescription) {
      setRealTime(true);

      try {
        const unsubscribe = artemisClient.subscribe(
          "TICKER",
          quote.realTimeTickerDescription,
          (data) => {
            formatAndMergeRealTimeData(data);
          }
        );

        return () => unsubscribe();
      } catch (e) {
        console.log("failed to get subscription data from artemis");

        return undefined;
      }
    }

    return undefined;
  }, [artemisClient, quote]);

  if (!quoteData) {
    return <NextError statusCode={404} />;
  }

  const { id, mediaSecurityType, mediaSecuritySubtype } = quoteData;

  const fortressOptions = {
    articleInfo: {
      contentId: `${PAGE_TYPE}/${id}`,
      metadata: {
        isMetered: !(locale === "ja" || locale === "jp"),
      },
      resourceType: mediaSecurityType,
      resourceSubtype: mediaSecuritySubtype,
      type: CONTENT_TYPE,
    },
    paywallData: {
      type: payWallType,
    },
  };

  const gtmSecurityType = quote?.type
    ? SECURITY_TYPES_TO_NAMES[quote.type] || quote.type
    : "unknown";

  const analytics = {
    adcode: "markets/quote",
    brand: "markets",
    contentType: `marketdata-quote-${gtmSecurityType}`,
    pageType: "marketdata",
    template: "bazaar-marketdata-html",
  } as unknown as null;

  const pageDataProps = {
    ...props,
    fortressOptions,
    analytics,
    pageId: id,
    contentType: mediaSecurityType,
    metadata: getQuoteStructuredData(quoteContextValue),
  };

  return (
    <>
      <PageWrapper {...pageDataProps}>
        <QuoteCompareProvider initialQuote={quoteData}>
          <SnapshotsChartsProvider>
            <QuoteContext.Provider value={quoteContextValue}>
              <QuickAddProvider watchlistServiceUrl={WATCHLIST_SERVICE_URL}>
                <AbbaProviderWithUserInfo platform="quote">
                  <Layout
                    leftRail={<LeftRail />}
                    contentWell={<QuotePageLayout />}
                    rightRail={<RightRail />}
                    recirc={
                      <PersonalizedRecircBoxSmallList ticker={quoteData.id} />
                    }
                  />
                </AbbaProviderWithUserInfo>
                {/* Plug-in client placeholder */}
                <div className="plug-banner" />
              </QuickAddProvider>
            </QuoteContext.Provider>
          </SnapshotsChartsProvider>
        </QuoteCompareProvider>
      </PageWrapper>
    </>
  );
}
